import * as React from 'react'
import styled from 'styled-components'
import * as constants from '../../../static/constants'
import PartnerVenueHeader from 'components/organisms/PartnerVenueHeader'

interface ICell {
  count: number
  max: number | null
}

interface IRow {
  label: string
  time: string
  cells: { [date: string]: ICell }
}

interface IProps {
  venue_name: string
  slug: string
  guest_list_active: boolean
  month: string // YYYY-MM
  today: string // YYYY-MM-DD
  rows: IRow[]
  daily_totals: { [date: string]: number }
  month_total: number
}

const WEEKDAY_LABELS = ['日', '月', '火', '水', '木', '金', '土']

const PartnerVenueShow: React.FC<IProps> = props => {
  const { slug, month, today, rows, daily_totals, month_total } = props
  const year = Number(month.split('-')[0])
  const monthNumber = Number(month.split('-')[1])

  const days: Array<{ date: string; day: number; weekday: string }> = []
  const lastDay = new Date(year, monthNumber, 0).getDate()
  for (let day = 1; day <= lastDay; day += 1) {
    days.push({
      date: `${month}-${String(day).padStart(2, '0')}`,
      day,
      weekday: WEEKDAY_LABELS[new Date(year, monthNumber - 1, day).getDay()],
    })
  }

  // 初期表示時に当日列まで横スクロールする
  const containerRef = React.useRef<HTMLDivElement>(null)
  React.useEffect(() => {
    const container = containerRef.current
    if (!container) {
      return
    }
    const todayCell = container.querySelector('th[data-today="true"]') as HTMLElement
    const nameCell = container.querySelector('th.nameCol') as HTMLElement
    if (todayCell && nameCell) {
      container.scrollLeft = todayCell.offsetLeft - nameCell.offsetWidth
    }
  }, [])

  return (
    <Wrapper>
      <PartnerVenueHeader
        {...props}
        active="calendar"
        monthPath={`/partner_venues/${slug}`}
        summary={
          <span className="calendarSummary">
            <span>{monthNumber}</span>月の予約合計<strong>{month_total}</strong>
          </span>
        }
      />
      <div className="tableContainer" ref={containerRef}>
        <table>
          <thead>
            <tr>
              <th className="nameCol">ツアー名</th>
              {days.map(({ date, day, weekday }) => (
                <th
                  key={date}
                  className={date === today ? 'today' : ''}
                  data-today={date === today}
                >
                  {date === today && <div className="todayIcon">今日</div>}
                  {monthNumber} / {day}
                  <span className="weekday">{weekday}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                <th className="nameCol">
                  <span className="tourName">{row.label}</span>
                  <span className="tourTime">{row.time}</span>
                </th>
                {days.map(({ date }) => {
                  const cell = row.cells[date]
                  // 予約0かつ定員0(グループ未作成)の日はTourがない日と同じ「-」表示にする
                  const isEmpty = cell === undefined || (cell.count === 0 && cell.max === 0)
                  return (
                    <td key={date} className={date === today ? 'today' : ''}>
                      {isEmpty ? (
                        '-'
                      ) : (
                        <>
                          <span className="reservationCount">{cell.count}</span>
                          <span className="maxAttendees">
                            {' '}
                            / {cell.max === null ? '-' : cell.max}
                          </span>
                        </>
                      )}
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th className="nameCol">合計</th>
              {days.map(({ date }) => (
                <td key={date} className={date === today ? 'today' : ''}>
                  <strong>{daily_totals[date]}</strong>
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>
    </Wrapper>
  )
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  box-sizing: border-box;
  padding: env(safe-area-inset-top) env(safe-area-inset-right) env(safe-area-inset-bottom)
    env(safe-area-inset-left);
  font-size: 12px;
  color: ${constants.COLORS.black};
  @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
    font-size: 14px;
  }

  .tableContainer {
    flex-grow: 1;
    min-height: 0;
    overflow: auto;
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    touch-action: pan-x pan-y;

    table {
      border-collapse: separate;
      border-spacing: 0;
      white-space: nowrap;
    }

    th,
    td {
      padding: 8px 12px;
      border-bottom: 1px solid ${constants.COLORS.divider2};
      border-right: 1px solid ${constants.COLORS.divider2};
      background: ${constants.COLORS.white};
      text-align: center;
      font-weight: normal;
      @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
        padding: 12px 16px;
      }
    }

    /* 日付ヘッダー行: 縦スクロール時固定 */
    thead th {
      position: sticky;
      top: 0;
      z-index: 2;
      font-size: 12px;
      line-height: 16px;
      background: ${constants.COLORS.background};
      min-width: 64px;
      padding: 16px 4px 8px;
      @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
        font-size: 14px;
        line-height: 20px;
        padding: 20px 12px 8px;
      }
      &.nameCol {
        padding: 8px 16px;
      }
      span.weekday {
        display: block;
        font-size: 12px;
        color: ${constants.COLORS.subText};
        font-weight: bold;
        @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
          font-size: 14px;
        }
      }
    }

    tbody td {
      color: ${constants.COLORS.divider};
    }

    /* 合計行: 縦スクロール時固定 */
    tfoot th,
    tfoot td {
      position: sticky;
      bottom: 0;
      z-index: 2;
      font-size: 15px;
      padding: 16px 12px;
      background: ${constants.COLORS.background};
      border-top: 1px solid ${constants.COLORS.divider2};
      @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
        font-size: 16px;
        padding: 20px 16px;
      }
      &.today {
        border-top: 2px solid ${constants.COLORS.primary};
      }
    }

    /* ツアー名列: 横スクロール時固定 */
    .nameCol {
      position: sticky;
      left: 0;
      z-index: 1;
      min-width: 140px;
      max-width: 180px;
      padding: 8px 16px;
      font-size: 12px;
      background: ${constants.COLORS.background};
      text-align: left;
      white-space: normal;
      @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
        font-size: 14px;
        padding: 12px 16px;
      }
    }

    thead .nameCol,
    tfoot .nameCol {
      z-index: 3;
    }

    tbody .nameCol {
      .tourName {
        display: block;
        color: ${constants.COLORS.subText};
      }

      .tourTime {
        display: block;
        font-weight: 600;
      }
    }

    .today {
      background: #effef7;
    }

    thead th.today {
      color: ${constants.COLORS.primary};
      font-weight: 600;

      .todayIcon {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        padding: 2px 0;
        background: ${constants.COLORS.primary};
        color: ${constants.COLORS.white};
        font-size: 10px;
        line-height: 12px;
        @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
          font-size: 12px;
          line-height: 16px;
        }
      }
    }

    td span {
      &.reservationCount {
        color: ${constants.COLORS.black};
        font-size: 14px;
        font-weight: 600;
        @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
          font-size: 16px;
        }
      }
      &.maxAttendees {
        color: ${constants.COLORS.subText};
        font-size: 11px;
        @media (min-width: ${constants.BREAKPOINT_TABLET}px) {
          font-size: 12px;
        }
      }
    }
  }
`

export default PartnerVenueShow
